/**
 * Performance Utilities
 * Throttling and optimization helpers for timeline components
 *
 * Note: Generic debounce has been moved to utils/formatters.ts
 * Dead code (clearPathCache) has been removed.
 */

import { useCallback, useRef, useEffect } from 'react';

/**
 * Internal throttle function - limits execution frequency
 * Not exported directly, used by useThrottledCallback hook
 */
function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;

  return function (this: any, ...args: Parameters<T>): void {
    if (!inThrottle) {
      inThrottle = true;
      func.apply(this, args);
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
}

/**
 * React hook for throttled callback
 * Used by timeline mouse tracking
 */
export function useThrottledCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number,
  deps: React.DependencyList = []
): (...args: Parameters<T>) => void {
  const throttledFn = useRef<((...args: Parameters<T>) => void) | null>(null);

  useEffect(() => {
    throttledFn.current = throttle(callback, delay);
  }, [callback, delay]);

  return useCallback((...args: Parameters<T>) => {
    if (throttledFn.current) {
      throttledFn.current(...args);
    }
  }, deps);
}

/**
 * Memoize SVG path calculations for timeline rendering
 * Includes automatic cache size management to prevent memory leaks
 */
const pathCache = new Map<string, string>();

export const memoizePath = (
  key: string,
  generator: () => string
): string => {
  const cached = pathCache.get(key);
  if (cached !== undefined) {
    return cached;
  }

  const path = generator();

  // Limit cache size to prevent memory leaks
  if (pathCache.size > 1000) {
    const firstKey = pathCache.keys().next().value;
    if (firstKey !== undefined) {
      pathCache.delete(firstKey);
    }
  }

  pathCache.set(key, path);
  return path;
};
